import React, { useEffect, useState } from 'react'
import { Table, Spin, notification, Input, Space, Button } from 'antd'
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { SUPER_ADMIN } from '../../../Constants/Roles/roles'
import { getAllCertificates, getStudentCertificates } from '../../../Constants/Api/functions/function'
import './style.scss'

const Certificates = () => {
    const [api, contextHolder] = notification.useNotification()
    const [isLoading, setIsLoading] = useState(false)
    const [certificates, setCertificates] = useState([])
    const [filteredCertificates, setFilteredCertificates] = useState([])
    const [searchText, setSearchText] = useState('')
    const userProfile = useSelector(state => state.userProfile.userProfile)

    useEffect(() => {
        const handleGetAllCertificates = async () => {
            setIsLoading(true)
            try {
                const data = await getAllCertificates()
                setCertificates(data)
                setFilteredCertificates(data)
            } catch (error) {
                console.error('handleGetAllCertificates', error)
                api.error({
                    message: 'Error',
                    description: 'Something went wrong'
                })
            }
            setIsLoading(false)
        }

        const handleGetStudentCertificates = async () => {
            setIsLoading(true)
            try {
                const data = await getStudentCertificates(userProfile?.id)
                setCertificates(data)
                setFilteredCertificates(data)
            } catch (error) {
                console.error('handleGetStudentCertificates', error)
                api.error({
                    message: 'Error',
                    description: 'Something went wrong'
                })
            }
            setIsLoading(false)
        }

        const fetchCertificates = async () => {
            if (userProfile?.role === SUPER_ADMIN) {
                await handleGetAllCertificates()
            } else {
                await handleGetStudentCertificates()
            }
        }
        
        if (userProfile?.id) {
            fetchCertificates()
        }
    }, [userProfile?.id, userProfile?.role, api])

    const handleSearch = (value) => {
        setSearchText(value)
        
        if (!value.trim()) {
            setFilteredCertificates(certificates)
            return
        }

        const filtered = certificates.filter(cert =>
            cert?.studentName?.toLowerCase().includes(value.toLowerCase()) ||
            cert?.courseName?.toLowerCase().includes(value.toLowerCase()) ||
            cert?.CertificateNumber?.toLowerCase().includes(value.toLowerCase()) ||
            cert?.StudentEmail?.toLowerCase().includes(value.toLowerCase())
        )
        
        setFilteredCertificates(filtered)
    }

    const handleDownloadCertificate = (certificateUrl) => {
        if (certificateUrl) {
            window.open(certificateUrl, '_blank')
        } else {
            api.error({
                message: 'Error',
                description: 'Certificate URL not available'
            })
        }
    }

    const columns = [
        {
            title: 'Certificate Number',
            dataIndex: 'CertificateNumber',
            key: 'CertificateNumber',
            width: 150,
            sorter: (a, b) => (a.CertificateNumber || '').localeCompare(b.CertificateNumber || '')
        },
        {
            title: 'Student Name',
            dataIndex: 'studentName',
            key: 'studentName',
            width: 150,
            sorter: (a, b) => (a.studentName || '').localeCompare(b.studentName || '')
        },
        ...(userProfile?.role === SUPER_ADMIN ? [{
            title: 'Student Email',
            dataIndex: 'StudentEmail',
            key: 'StudentEmail',
            width: 180,
            sorter: (a, b) => (a.StudentEmail || '').localeCompare(b.StudentEmail || '')
        }] : []),
        {
            title: 'Course Name',
            dataIndex: 'courseName',
            key: 'courseName',
            width: 180,
            sorter: (a, b) => (a.courseName || '').localeCompare(b.courseName || '')
        },
        {
            title: 'Issue Date',
            dataIndex: 'IssueDate',
            key: 'IssueDate',
            width: 120,
            sorter: (a, b) => new Date(a.IssueDate) - new Date(b.IssueDate),
            render: (text) => text || '-'
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Button
                    type='primary'
                    size='small'
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadCertificate(record.CertificateURL)}
                >
                    View
                </Button>
            )
        }
    ]

    return (
        <div className='certificates-container'>
            {contextHolder}
            
            <div className='certificates-header'>
                <h1>
                    {userProfile?.role === SUPER_ADMIN 
                        ? 'All Certificates' 
                        : 'My Certificates'}
                </h1>
            </div>

            <div className='certificates-search'>
                <Space>
                    <Input
                        placeholder='Search by certificate number, student name, or course...'
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => handleSearch(e.target.value)}
                        style={{ width: '400px' }}
                        allowClear
                    />
                </Space>
            </div>

            <Spin spinning={isLoading} tip='Loading certificates...'>
                <Table
                    columns={columns}
                    dataSource={filteredCertificates}
                    rowKey='CertificateID'
                    pagination={{
                        pageSize: 10,
                        total: filteredCertificates.length,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} certificates`
                    }}
                    scroll={{ x: 1000 }}
                    className='certificates-table'
                />
            </Spin>
        </div>
    )
}

export default Certificates
